import { push } from '@lagunovsky/redux-react-router'
import HomeAction from 'home/actions/HomeAction'
import { isUndefined, last, isNaN, compact, includes } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { STATION_NAME_ASSOCIATION, STATION_TYPE, STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import ToastrAction from 'toastr/actions/ToastrAction'
import { getFormatedFileName } from 'utils/FileUtils'
import { H_STATION_QUALITO_OPERATIONS } from '../../../../account/constants/AccessRulesConstants'
import ProgressCard from '../../../../components/card/ProgressCard'
import Button from '../../../../components/forms/Button'
import StationAction from '../../../../station/actions/StationAction'
import { getDate } from '../../../../utils/DateUtil'
import { setModal } from '../../../../utils/FormUtils'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { getStationTitleWithTownName } from '../../../../utils/StationUtils'
import { getLabel } from '../../../../utils/StoreUtils'
import OperationAction from '../actions/OperationAction'
import { ANALYSIS, BILAN, DESCRIPTION, DIAGRAPHIE, ENVIRONMENT, INDICES } from '../constants/OperationDescriptionConstants'
import OperationAnalysisPanel from './analysis/OperationAnalysisPanel'
import OperationOverviewPanel from './chart/OperationOverviewPanel'
import OperationDescriptionPanel from './description/OperationDescriptionPanel'
import EnvironmentalConditionsPanel from './environmentalCondition/EnvironmentalConditionsPanel'
import OperationIndicesPanel from './indices/OperationIndicesPanel'
import DtoSample from '../../../dto/DtoSample'
import queryString from 'query-string'
import { useLocation, useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import { Stack } from '@mui/material'
import Tabs from 'components/Tabs'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { AttachFile } from '@mui/icons-material'
import DiagraphieOverviewPanel from './chart/DiagraphieOverviewPanel'

const tabs = {
    BILAN: 'BILAN',
    DESCRIPTION: 'DESCRIPTION',
    ANALYSIS: 'ANALYSIS',
    INDICES: 'INDICES',
    DIAGRAPHIE: 'DIAGRAPHIE',
    ENVIRONMENT: 'ENVIRONMENT',
}

const StationOperationApp = ({}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const [sample, setSample] = useState() // sample of the operation (prelevements)
    const [samplings, setSamplings] = useState([]) // samplings of operation (prelevements_echantillons)
    const [networks, setNetworks] = useState([]) // networks of operation (qualitometres_points_operations_reseaux)

    const {
        accountUser,
        qualitometer,
        operation,
        operationAnalysis,
        remarks,
        supports,
        files,
        qualityIndicators,
        citiesIndex,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        qualitometer: store.QualityReducer.qualitometer,
        operation: store.OperationReducer.operation,
        operationAnalysis: store.OperationReducer.operationAnalysis,
        remarks: store.OperationReducer.remarks,
        supports: store.SupportReducer.supports,
        files: store.OperationReducer.files,
        qualityIndicators: store.QualityReducer.qualityIndicators,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const thresholdInSettings = useApplicationSetting('SEUILS')
    const foundFirstIndicatorThreshold = useMemo(() => (qualityIndicators.find(q => q.stationType == qualitometer.stationType)?.indicators || [])?.[0]?.threshold, [qualityIndicators, qualitometer])

    const [threshold, setThreshold] = useState((isNaN(thresholdInSettings) ? undefined : thresholdInSettings) || foundFirstIndicatorThreshold)
    const defaultThreshold= (isNaN(thresholdInSettings) ? undefined : thresholdInSettings) || foundFirstIndicatorThreshold

    const isDiagraphie = parseInt(qualitometer.stationType) === STATION_TYPE.DIAGRAPHIE

    const location = useLocation()
    const params = useParams()
    const dispatch = useDispatch()

    const townName = useMemo(() => citiesIndex[qualitometer.townCode]?.name, [citiesIndex, qualitometer])

    useTitle(() => {
        const {
            id,
            code,
        } = params
        return [{
            title: i18n.quality,
            href: 'quality',
        }, {
            title: getStationTitleWithTownName(qualitometer, townName),
            href: `station/quality/${id}`,
        }, {
            title: i18n.operations,
            href: `station/quality/${id}/operation`,
        }, {
            title: code === 'new' ? i18n.newOperation : `${i18n.operation} : ${getDate(operation.date)} - ${getLabel(supports, operation.support)}`,
            href: `station/quality/${id}/operation/${code}`,
        }]
    }, [qualitometer, operation, supports, townName])

    useEffect(() => {
        if (!componentHasHabilitations(H_STATION_QUALITO_OPERATIONS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
        dispatch(OperationAction.loadOperationApp(params.code, params.id, threshold, p => setProgress(p))).then(s => {
            OperationAction.promiseSampleSamplings(parseInt(params.id), s?.id).then(samplingsList => {
                OperationAction.promiseOperationNetworks(params.id, params.code).then(n => {
                    setSample(new DtoSample(s))
                    setSamplings(samplingsList)
                    setNetworks(n)
                    setDataLoaded(true)
                })
            })
        })
        if (params.code !== 'new') {
            dispatch(OperationAction.fetchFiles(params.id, params.code))
        }
        dispatch(HomeAction.setHelpLink('qualite', '29'))
        return () => dispatch(OperationAction.fetchResetSingle())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const reloadOperation = () => {
        dispatch(OperationAction.fetchOperation(params.code, params.id))
        OperationAction.promiseSample(params.id, params.code).then(s => setSample(s))
        dispatch(OperationAction.fetchAnalysis(parseInt(params.code), parseInt(params.id), threshold))
    }

    const getOperationFiles = () => {
        setModal({
            title: i18n.analysisDocuments,
            content: (
                <div>
                    {
                        files.map(fUrl => {
                            const name = last(fUrl.split('/'))
                            return { name, url: fUrl }
                        }).map((f, i) => {
                            return (
                                <Stack key={i}>
                                    <a href={ f.url } target='_blank' className='modal-close flex-row valign-wrapper'><AttachFile /><h5>{ f.name }</h5></a>
                                </Stack>
                            )
                        })
                    }
                    <Stack>
                        <Button title={ i18n.close } className='left red modal-close'/>
                    </Stack>
                </div>
            ),
        })
    }

    const uploadFile = e => {
        const [file] = e.target.files
        const reader = new FileReader()
        reader.onload = upload => {
            const {
                code = '',
                id: stationId,
            } = qualitometer
            const {
                analysisNumber = '',
                refAnalysis = '',
                id: operationId,
                date,
            } = operation
            const fileNameSplit = file.name.split('.')
            const ext = fileNameSplit.slice(fileNameSplit.length - 1)
            const objFile = {
                content: upload.target.result,
                name: getFormatedFileName(code, undefined, `${analysisNumber}_${refAnalysis}`, ext, undefined, date, 'quality', false),
                stationType: STATION_TYPE_CONSTANT[STATION_NAME_ASSOCIATION.quality],
                stationId,
                path: '',
            }
            dispatch(StationAction.addStationFile(objFile, 'doc')).then(() => dispatch(OperationAction.fetchFiles(stationId, operationId)))
        }
        reader.onerror = () => {
            dispatch(ToastrAction.toastrError(i18n.importError))
        }
        reader.readAsDataURL(file)
    }

    const onChange = (changes) => {
        if (changes.samplings) {
            setSamplings(changes.samplings)
        }
        if (changes.networks) {
            setNetworks(changes.networks)
        }
    }

    const getPanel = (panel) => {
        switch (panel) {
            case DIAGRAPHIE:
                return (
                    <DiagraphieOverviewPanel
                        id={params.id}
                        threshold={threshold}
                        files={files}
                        getOperationFiles={getOperationFiles}
                        uploadFile={uploadFile}
                        sample={sample}
                        onChangeThreshold={setThreshold}
                    />
                )
            case BILAN:
                return (
                    <OperationOverviewPanel
                        id={params.id}
                        threshold={threshold}
                        files={files}
                        getOperationFiles={getOperationFiles}
                        uploadFile={uploadFile}
                        sample={sample}
                        onChangeThreshold={setThreshold}
                    />
                )
            case DESCRIPTION:
                return (
                    <OperationDescriptionPanel
                        id={params.id}
                        code={params.code}
                        uploadFile={uploadFile}
                        files={files}
                        getOperationFiles={getOperationFiles}
                        sample={sample}
                        reloadOperation={reloadOperation}
                        changeParent={onChange}
                        samplings={samplings}
                        networks={networks}
                    />
                )
            case ANALYSIS:
                return (
                    <OperationAnalysisPanel
                        id={params.id}
                        code={params.code}
                        operation={operation}
                        analysis={operationAnalysis}
                        remarks={remarks}
                        threshold={threshold}
                        onChangeThreshold={setThreshold}
                        uploadFile={uploadFile}
                        files={files}
                        getOperationFiles={getOperationFiles}
                        sampleId={sample?.id}
                        defaultThreshold={defaultThreshold}
                        samplings={samplings}
                    />
                )
            case INDICES:
                return (
                    <OperationIndicesPanel
                        qualitometerId={operation.qualitometer}
                        operationId={operation.id}
                        uploadFile={uploadFile}
                        files={files}
                        getOperationFiles={getOperationFiles}
                    />
                )
            case ENVIRONMENT:
                return (
                    <EnvironmentalConditionsPanel
                        uploadFile={uploadFile}
                        files={files}
                        getOperationFiles={getOperationFiles}
                        operation={operation}
                    />
                )
            default:
                return null
        }
    }

    if (dataLoaded) {
        const { code } = params
        const tabList = compact([
            code !== 'new' ? {
                constant: isDiagraphie ? tabs.DIAGRAPHIE : tabs.BILAN,
                label: isDiagraphie ? i18n.diagraphies : i18n.overview,
            }: undefined,
            {
                constant: tabs.DESCRIPTION,
                label: i18n.descriptive,
            },
            {
                constant: tabs.ANALYSIS,
                label: i18n.analysis,
            },
            isDiagraphie ? undefined : {
                constant: tabs.INDICES,
                label: i18n.indices,
            },
            isDiagraphie ? undefined : {
                constant: tabs.ENVIRONMENT,
                label: i18n.environmentalConditions,
            },
        ])
        const orderedTabs = accountUser.labo === '1' ? tabList.reverse() : tabList
        const { tab } = queryString.parse(location.search)
        const searchTab = !isUndefined(tab) && includes(tabList.map(t => t.constant), tab) ? tab : undefined
        return (
            <Stack padding={2}>
                <Tabs
                    // defaultTab : if new operation => BILAN not allowed, if a tab is searched in the url and in the tabList => it's the default tab, else => either BILAN or DESCRIPTION if new operaption
                    defaultTab={searchTab || (accountUser.labo === '1' ? tabs.ANALYSIS : tabList[0].constant)}
                    tabs={orderedTabs}
                >
                    {t => getPanel(t)}
                </Tabs>
            </Stack>
        )
    }
    return <Stack padding={2}><ProgressCard progress={progress} /></Stack>
}

export default StationOperationApp